import type React from 'react'
import {forwardRef} from 'react'
import {clsx} from 'clsx'
import styles from './ToggleButton.module.css'

interface ToggleButtonProps {
  icon?: React.ReactNode
  isSelected?: boolean
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  hasMenu?: boolean
  isMenuOpen?: boolean
  selectedItemLabel?: string
  children: React.ReactNode
}

export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  (
    {
      icon,
      isSelected = false,
      onClick,
      hasMenu = false,
      isMenuOpen = false,
      selectedItemLabel,
      children,
      ...otherProps
    },
    ref,
  ) => {
    // Extract aria attributes from otherProps
    const ariaAttributes = Object.keys(otherProps).reduce(
      (acc, key) => {
        if (key.startsWith('aria-')) {
          acc[key] = otherProps[key as keyof typeof otherProps]
        }
        return acc
      },
      {} as Record<string, string | boolean | undefined>,
    )

    // Determine the appropriate aria attributes based on component state
    const getAriaAttributes = () => {
      if (!hasMenu) {
        // Simple toggle button
        return {'aria-pressed': isSelected}
      } else {
        // Menu trigger button
        const attributes: Record<string, string | boolean | undefined> = {
          'aria-haspopup': 'menu',
          'aria-expanded': isMenuOpen,
        }

        return attributes
      }
    }

    // Merge component-specific aria attributes with passed aria attributes
    const allAriaAttributes = {
      ...ariaAttributes,
      ...getAriaAttributes(),
    }

    // Determine what text to display
    // For menu buttons, selectedItemLabel shows the selected menu item
    // For regular toggle buttons, children is always used
    const displayText = hasMenu && selectedItemLabel ? selectedItemLabel : children

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(event)
      }
    }

    return (
      <button
        ref={ref}
        type="button"
        className={clsx(styles.toggleButton, isSelected && styles.selected)}
        onClick={handleClick}
        {...allAriaAttributes}
      >
        {!!icon && <span className={styles.icon}>{icon}</span>}
        <span className={styles.textContent}>{displayText}</span>
      </button>
    )
  },
)

ToggleButton.displayName = 'ToggleButton'
